import type { GameEnvelope, RoomView } from '@gmp/shared-types/browser';
import type { SoundCue, SoundScope } from '@gmp/shared-ui';

/** A cue the platform plays in reaction to a room change, and who it is for. */
export interface RoomSound {
  cue: SoundCue | 'countdown';
  scope: SoundScope;
}

export interface RoomSnapshot {
  room: RoomView | null;
  game: GameEnvelope | null;
}

const humans = (r: RoomView) => r.members.filter((m) => !m.spectator && !m.bot);
const players = (r: RoomView) => r.members.filter((m) => !m.spectator);

/** Everyone connected (at least two players, AI players count as ready) has pressed Ready. */
export function allReady(r: RoomView): boolean {
  const ps = players(r).filter((m) => m.connected || m.bot);
  return ps.length >= 2 && humans(r).some((m) => m.connected) && ps.every((m) => m.bot || m.ready);
}

/**
 * The platform's sound bites, from two consecutive snapshots of the room as this device sees it. Pure, so
 * the event-to-sound mapping is unit tested. `me` is this device's player (null on the TV).
 *
 *  - room cues (the TV plays them, or every phone when there is no TV): someone joined or left, someone
 *    else readied up or backed out, everyone's ready, the game starts (countdown + go), a reveal, a round's
 *    results, the final results (fanfare on the TV), someone opted in to a rematch.
 *  - personal cues (always on your own phone): your round won or lost, your final result, a join request
 *    for the host.
 * Your own taps (ready, lock in, rematch) play where you tap them, not here.
 */
export function roomSounds(prev: RoomSnapshot, next: RoomSnapshot, me: string | null): RoomSound[] {
  const a = prev.room;
  const b = next.room;
  // You just got into a party (joined, or let in by the host).
  if (b && me && b.you.role !== 'display' && (!a || a.code !== b.code)) return [{ cue: 'join', scope: 'personal' }];
  if (!a || !b || a.code !== b.code) return [];
  const out: RoomSound[] = [];
  const room = (cue: RoomSound['cue']) => out.push({ cue, scope: 'room' });
  const personal = (cue: RoomSound['cue']) => out.push({ cue, scope: 'personal' });

  // Membership and readiness: lobby only (people reconnecting mid-game aren't news).
  const before = new Map(a.members.map((m) => [m.id, m]));
  const after = new Map(b.members.map((m) => [m.id, m]));
  if (b.stage === 'LOBBY' || b.stage === 'FINAL_RESULTS') {
    const joined = b.members.some((m) => !before.has(m.id) && m.id !== me && !m.spectator);
    const left = a.members.some((m) => !after.has(m.id) && m.id !== me && !m.spectator);
    if (joined) room('join');
    else if (left) room('leave');
  }
  if (b.stage === 'LOBBY' && a.stage === 'LOBBY') {
    if (!allReady(a) && allReady(b)) room('allReady');
    else {
      let up = false;
      let down = false;
      for (const m of b.members) {
        const was = before.get(m.id);
        if (!was || m.id === me || m.bot) continue;
        if (!was.ready && m.ready) up = true;
        if (was.ready && !m.ready) down = true;
      }
      if (up) room('ready');
      else if (down) room('unready');
    }
  }
  if (b.you.isHost && b.pendingJoins.some((p) => !a.pendingJoins.some((q) => q.id === p.id))) personal('invite');

  // The game starts.
  if (b.stage === 'IN_GAME' && a.stage !== 'IN_GAME') room('countdown');

  // Phase changes inside a game.
  const pa = prev.game?.phase;
  const pb = next.game?.phase;
  if (b.stage === 'IN_GAME' && a.stage === 'IN_GAME' && pa && pb && next.game?.sessionId === prev.game?.sessionId && (pa.name !== pb.name || pa.round !== pb.round)) {
    if (pb.name === 'REVEAL') room('reveal');
    if (pb.name === 'ROUND_RESULTS') {
      const scores = next.game!.scores;
      const mine = me ? scores.find((s) => s.playerId === me) : undefined;
      const best = Math.max(0, ...scores.map((s) => s.lastRound));
      if (mine && best > 0) personal(mine.lastRound === best ? 'roundWin' : 'roundLose');
      else if (!mine && best > 0) room('roundWin');
    }
  }

  // Final results: a fanfare on the TV, and on each phone that player's own result.
  if (b.stage === 'FINAL_RESULTS' && a.stage !== 'FINAL_RESULTS' && b.lastResults) {
    const ranking = b.lastResults.ranking;
    const mine = me ? ranking.find((r) => r.playerId === me) : undefined;
    // Winner: fanfare. Top half: a small win jingle. Bottom half: the playful sad trombone.
    if (mine) personal(mine.rank === 1 ? 'gameWin' : mine.rank <= Math.ceil(ranking.length / 2) ? 'roundWin' : 'gameLose');
    else room('gameWin');
  }

  // Grudge Match: someone else opted in to the rematch.
  const inBefore = new Set(a.rematch?.optedIn ?? []);
  if (b.rematch && b.rematch.optedIn.some((id) => !inBefore.has(id) && id !== me)) room('rematch');
  return out;
}
